import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import {
    PAY_SHORTCUT,
    renderWithKeyShortcuts,
    ShortcutHint,
    VOID_SHORTCUT,
} from '@/components/shortcuts';
import type { Discount } from '../../discountMath';
import { ShiftGateNotice } from '../../shift/ShiftGateNotice';
import { CartTotals } from './CartTotals';
import type { CartNotice } from './useCartPanel';

const renderPayButton = renderWithKeyShortcuts(
    'button',
    PAY_SHORTCUT.combination,
);

const renderVoidButton = renderWithKeyShortcuts(
    'button',
    VOID_SHORTCUT.combination,
);

type CartFooterProps = {
    subtotal: number;
    lineDiscountTotal: number;
    saleDiscount: Discount | null;
    saleDiscountAmount: number;
    isSaleDiscountInvalid: boolean;
    isLineDiscountInvalid: boolean;
    total: number;
    discountNotice: CartNotice | null;
    onSaleDiscount: () => void;
    isEmpty: boolean;
    /** A non-empty cart, an open shift and every discount fitting: HOLD and PAY are enabled. */
    canPay: boolean;
    onHold: () => void;
    /** Opens the void confirm, never clears directly. */
    onVoid: () => void;
    onPay: () => void;
};

/**
 * The cart's totals (`CartTotals`: subtotal, discounts, the hero total) over
 * the shift gate notice and the HOLD / VOID / PAY action row, with the
 * `Ctrl+Del` and `F9` hints inside their buttons.
 * Each of those buttons names its own key with `aria-keyshortcuts`; the
 * hint inside it stays out of its name.
 */
export const CartFooter: React.FC<CartFooterProps> = ({
    subtotal,
    lineDiscountTotal,
    saleDiscount,
    saleDiscountAmount,
    isSaleDiscountInvalid,
    isLineDiscountInvalid,
    total,
    discountNotice,
    onSaleDiscount,
    isEmpty,
    canPay,
    onHold,
    onVoid,
    onPay,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex-shrink-0 border-t border-border px-5 py-3">
            <CartTotals
                discountNotice={discountNotice}
                isEmpty={isEmpty}
                isLineDiscountInvalid={isLineDiscountInvalid}
                isSaleDiscountInvalid={isSaleDiscountInvalid}
                lineDiscountTotal={lineDiscountTotal}
                onSaleDiscount={onSaleDiscount}
                saleDiscount={saleDiscount}
                saleDiscountAmount={saleDiscountAmount}
                subtotal={subtotal}
                total={total}
            />

            <div className="mt-4 flex flex-col gap-2">
                <ShiftGateNotice />
                <div className="flex gap-2">
                    <Button
                        className="flex-1"
                        {...getNodeTestIdProp('seller-hold')}
                        isDisabled={!canPay}
                        onPress={onHold}
                        variant="outline"
                    >
                        {t('seller.hold')}
                    </Button>
                    <Button
                        className="flex-1"
                        {...getNodeTestIdProp('seller-void')}
                        isDisabled={isEmpty}
                        onPress={onVoid}
                        render={renderVoidButton}
                        variant="danger-soft"
                    >
                        {t('seller.void')}
                        <ShortcutHint
                            placement="control"
                            shortcut={VOID_SHORTCUT}
                        />
                    </Button>
                </div>
                <Button
                    className="w-full"
                    {...getNodeTestIdProp('seller-pay')}
                    isDisabled={!canPay}
                    onPress={onPay}
                    render={renderPayButton}
                    size="lg"
                >
                    {t('seller.pay')}
                    <ShortcutHint placement="control" shortcut={PAY_SHORTCUT} />
                </Button>
            </div>
        </div>
    );
};
